import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Linkedin, Twitter, Sparkles, ArrowLeft } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import useSEO from '../hooks/useSEO';
import useJsonLd from '../hooks/useJsonLd';
import { SITE_CONFIG } from '../data/seoConfig';
import { AUTHORS, POST_AUTHORS } from '../data/authors';
import { BLOG_POSTS } from '../data/blog';

export default function Author() {
  const { slug } = useParams();
  const author = AUTHORS.find((a) => a.slug === slug);
  const canonical = author ? `${SITE_CONFIG.url}/authors/${author.slug}` : undefined;

  useSEO(
    author
      ? {
          title: `${author.name} — ${author.title} | Ransen`,
          description: `${author.name}, ${author.title} at Ransen. ${author.bio.slice(0, 120)}...`,
          canonical,
          keywords: `${author.name}, ${author.title}, digital marketing expert, ${(author.expertise || []).join(', ')}`,
        }
      : {}
  );

  useJsonLd(
    author ? `author-schema-${author.slug}` : 'author-schema',
    author
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'Person',
            '@id': `${SITE_CONFIG.url}/authors/${author.slug}#person`,
            name: author.name,
            jobTitle: author.title,
            description: author.bio,
            image: author.avatar,
            url: canonical,
            worksFor: { '@id': `${SITE_CONFIG.url}/#organization` },
            knowsAbout: author.expertise,
            sameAs: [author.linkedin, author.twitter].filter(Boolean),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_CONFIG.url },
              { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_CONFIG.url}/blog` },
              { '@type': 'ListItem', position: 3, name: author.name, item: canonical },
            ],
          },
        ]
      : null
  );

  if (!author) {
    return (
      <div className='pt-40 pb-32 text-center'>
        <h1 className='text-3xl text-white'>Author not found</h1>
        <Link to='/blog' className='mt-6 inline-block text-brand'>Back to blog</Link>
      </div>
    );
  }

  const authorPosts = BLOG_POSTS.filter((p) => POST_AUTHORS[p.slug] === author.slug);

  return (
    <div data-testid='author-page'>
      <PageHeader
        eyebrow={author.title}
        title={<span className='text-brand-gradient'>{author.name}</span>}
        description={author.bio}
        crumbs={[{ label: 'Blog', to: '/blog' }, { label: author.name }]}
      />

      <section className='py-10'>
        <div className='max-w-5xl mx-auto px-6'>
          <div className='glass-card rounded-2xl p-8 md:p-10 grid md:grid-cols-[160px_1fr] gap-8 items-start'>
            <div className='flex justify-center md:block'>
              <img
                src={author.avatar}
                alt={author.name}
                className='w-32 h-32 md:w-40 md:h-40 rounded-full object-cover ring-2 ring-brand/40'
              />
            </div>
            <div>
              <div className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/10 border border-brand/25 text-brand text-[12px] font-semibold uppercase tracking-wider'>
                <Sparkles className='w-3 h-3' />
                {author.experienceYears}+ years experience
              </div>
              <h2 className='mt-4 font-heading text-2xl md:text-3xl font-bold text-white'>{author.name}</h2>
              <div className='text-brand text-sm font-medium mt-1'>{author.title}</div>
              <p className='mt-4 text-white/75 text-[15.5px] leading-[1.75] font-body'>{author.bio}</p>

              <div className='mt-6 flex flex-wrap gap-2'>
                {(author.expertise || []).map((e) => (
                  <span key={e} className='px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/70 text-[12.5px]'>
                    {e}
                  </span>
                ))}
              </div>

              <div className='mt-6 flex flex-wrap gap-3'>
                {author.linkedin && (
                  <a
                    href={author.linkedin}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card text-white/80 hover:text-white text-[13px] font-medium'
                    data-testid='author-linkedin'
                  >
                    <Linkedin className='w-3.5 h-3.5' /> LinkedIn
                  </a>
                )}
                {author.twitter && (
                  <a
                    href={author.twitter}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card text-white/80 hover:text-white text-[13px] font-medium'
                    data-testid='author-twitter'
                  >
                    <Twitter className='w-3.5 h-3.5' /> Twitter
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Posts by this author */}
      {authorPosts.length > 0 && (
        <section className='py-14 border-t border-brand/10'>
          <div className='max-w-6xl mx-auto px-6'>
            <h3 className='font-heading text-2xl md:text-3xl font-bold text-white mb-8'>
              Articles by {author.name.split(' ')[0]}
            </h3>
            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-6'>
              {authorPosts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  className='glass-card rounded-2xl overflow-hidden group'
                  data-testid={`author-post-${p.slug}`}
                >
                  <div className='relative aspect-[16/9] overflow-hidden'>
                    <img src={p.image} alt={p.title} className='w-full h-full object-cover transition-transform duration-700 group-hover:scale-105' />
                    <div className='absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-white/85 text-[10.5px] tracking-wider uppercase font-accent'>
                      {p.category}
                    </div>
                  </div>
                  <div className='p-5'>
                    <h4 className='font-heading text-[15px] font-bold text-white leading-snug group-hover:text-brand transition-colors'>
                      {p.title}
                    </h4>
                    <div className='mt-3 text-white/50 text-[12px]'>{p.date} · {p.read}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className='py-10 text-center'>
        <Link to='/blog' className='inline-flex items-center gap-2 text-brand text-sm font-medium'>
          <ArrowLeft className='w-4 h-4' /> All articles
        </Link>
      </div>
    </div>
  );
}
